import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Upload, Camera, CheckCircle2, LogOut, ExternalLink } from "lucide-react";
import { portalApi, formatApiError } from "./api";
import { BRAND } from "@/data/brand";
import PhotoCropper from "./PhotoCropper";

const TOTAL_STEPS = 4;

export default function PortalWizard({ user: initialUser, onUpdate, onLogout, editMode = false, onDone }) {
  const [step, setStep] = useState(initialUser.first_name ? 1 : 1);
  const [user, setUser] = useState(initialUser);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null); // File awaiting crop
  const [submitted, setSubmitted] = useState(false);

  const patch = async (payload) => {
    setSaving(true);
    try {
      const { user: u, pending_review } = await portalApi.updateProfile(payload);
      setUser(u);
      onUpdate?.(u);
      return { user: u, pending_review };
    } catch (err) { toast.error(formatApiError(err)); throw err; }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (blobOrFile, filename = "photo.jpg") => {
    setSaving(true);
    try {
      // Convert Blob → File (backend needs a filename with extension)
      const file = blobOrFile instanceof File ? blobOrFile : new File([blobOrFile], filename, { type: blobOrFile.type || "image/jpeg" });
      const { photo_url, pending_review } = await portalApi.uploadPhoto(file);
      const u = { ...user, photo_url };
      setUser(u);
      onUpdate?.(u);
      setPhotoFile(null);
      toast.success(pending_review ? "Photo submitted for admin approval" : "Photo uploaded");
    } catch (err) { toast.error(formatApiError(err) || "Upload failed"); }
    finally { setSaving(false); }
  };

  const publish = async () => {
    setSaving(true);
    try {
      const { slug } = await portalApi.publish();
      const u = { ...user, published: true, slug };
      setUser(u);
      onUpdate?.(u);
      toast.success("Card published");
    } catch (err) { toast.error(formatApiError(err) || "Cannot publish yet"); }
    finally { setSaving(false); }
  };

  const submitForReview = async () => {
    // Push all editable fields — backend diffs vs. live values
    const payload = {};
    ["first_name","last_name","professional_title","phone","professional_email","license_number","languages","service_areas","bio"].forEach((k) => {
      if (user[k] !== undefined && user[k] !== null) payload[k] = user[k];
    });
    try {
      const { pending_review } = await patch(payload);
      if (pending_review) {
        toast.success("Changes submitted for admin approval");
        setSubmitted(true);
        setTimeout(() => onDone?.(), 1200);
      } else {
        toast.success("Profile saved");
        onDone?.();
      }
    } catch {}
  };

  const canAdvance =
    (step === 1 && user.first_name && user.last_name && user.phone && user.professional_email) ||
    (step === 2 && user.license_number) ||
    step === 3 ||
    step === 4;

  return (
    <main data-testid="portal-wizard" className="min-h-screen bg-midnight-deeper px-6 py-10 sm:py-16">
      <div className="max-w-lg mx-auto">
        <header className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src={BRAND.logoWhite} alt="" className="h-9 w-auto object-contain" />
            <div className="font-mono text-[0.55rem] text-gold/80 tracking-[0.3em]">{editMode ? "EDIT" : "SETUP"}</div>
          </div>
          <div className="flex items-center gap-4">
            {editMode && (
              <button onClick={onDone} className="text-white/60 hover:text-gold text-xs font-mono tracking-[0.22em] uppercase" data-testid="wizard-cancel">Cancel</button>
            )}
            <button onClick={onLogout} className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-xs font-mono tracking-[0.22em] uppercase" data-testid="wizard-logout">
              <LogOut className="w-3.5 h-3.5" /> Log Out
            </button>
          </div>
        </header>

        {editMode && (
          <div className="mb-6 border border-gold/30 bg-gold/5 p-3 text-[0.62rem] font-mono tracking-[0.22em] uppercase text-gold/90" data-testid="wizard-edit-notice">
            Editing published card — changes go to admin approval before appearing publicly
          </div>
        )}

        <div className="flex items-center justify-between mb-8">
          <div className="font-mono text-[0.62rem] text-gold tracking-[0.28em] uppercase" data-testid="wizard-step">Step {step} / 0{TOTAL_STEPS}</div>
          <div className="flex gap-1.5">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className={`h-px w-8 transition-all ${n <= step ? "bg-gold" : "bg-white/15"}`} />
            ))}
          </div>
        </div>

        <div className="glass-strong p-6 sm:p-8 space-y-6">
          {step === 1 && (
            <Step1 user={user} setUser={setUser} onPhoto={(f) => setPhotoFile(f)} uploading={saving && !!photoFile} />
          )}
          {step === 2 && <Step2 user={user} setUser={setUser} />}
          {step === 3 && <Step3 user={user} setUser={setUser} />}
          {step === 4 && <Step4 user={user} onPublish={publish} publishing={saving} editMode={editMode} onSubmitReview={submitForReview} submitted={submitted} />}

          <div className="flex gap-3 pt-4 border-t border-white/8">
            {step > 1 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="btn-ghost-blue inline-flex items-center gap-2 !py-3 !px-4 !text-[0.62rem]"
                data-testid="wizard-back"
              ><ArrowLeft className="w-3 h-3" /> Back</button>
            )}
            {step < 4 && (
              <button
                onClick={async () => {
                  const payload = {};
                  ["first_name","last_name","professional_title","phone","professional_email","license_number","languages","service_areas","bio"].forEach((k) => {
                    if (user[k] !== undefined && user[k] !== null) payload[k] = user[k];
                  });
                  try { await patch(payload); setStep((s) => s + 1); } catch {}
                }}
                disabled={!canAdvance || saving}
                className={`flex-1 inline-flex items-center justify-center gap-2 !py-3 !text-[0.62rem] ${canAdvance && !saving ? "btn-gold" : "border border-white/8 bg-white/5 text-white/30 font-display uppercase tracking-[0.16em]"}`}
                data-testid="wizard-next"
              >Continue <ArrowRight className="w-3 h-3" /></button>
            )}
          </div>
        </div>
      </div>

      {photoFile && (
        <PhotoCropper
          file={photoFile}
          saving={saving}
          onCancel={() => { if (!saving) setPhotoFile(null); }}
          onSave={(blob) => uploadPhoto(blob, (photoFile.name || "photo").replace(/\.[^.]+$/, "") + ".jpg")}
        />
      )}
    </main>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[0.6rem] text-ag-text-muted tracking-[0.28em] block mb-1 uppercase">{label}</span>
    {children}
  </label>
);

function Step1({ user, setUser, onPhoto, uploading }) {
  return (
    <>
      <h2 className="font-display text-2xl text-white font-light tracking-[-0.025em]">Basic information</h2>
      <p className="text-ag-text-secondary text-sm font-light">This appears on your public digital card.</p>

      <div className="flex items-center gap-5">
        <div className="w-20 h-20 rounded-full overflow-hidden border border-gold/30 bg-white/5 flex items-center justify-center">
          {user.photo_url ? (
            <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <Camera className="w-6 h-6 text-white/40" />
          )}
        </div>
        <label className="btn-ghost-blue inline-flex items-center gap-2 !py-2.5 !px-4 !text-[0.62rem] cursor-pointer">
          <Upload className="w-3.5 h-3.5" /> {uploading ? "Uploading…" : (user.photo_url ? "Change" : "Upload")}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files?.[0]) { onPhoto(e.target.files[0]); e.target.value = ""; } }} data-testid="wizard-photo-input" />
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="First Name *"><input className="ag-input" value={user.first_name || ""} onChange={(e) => setUser({ ...user, first_name: e.target.value })} data-testid="wizard-first-name" /></Field>
        <Field label="Last Name *"><input className="ag-input" value={user.last_name || ""} onChange={(e) => setUser({ ...user, last_name: e.target.value })} data-testid="wizard-last-name" /></Field>
      </div>
      <Field label="Professional Title">
        <input className="ag-input" value={user.professional_title || "Licensed Public Adjuster"} onChange={(e) => setUser({ ...user, professional_title: e.target.value })} data-testid="wizard-title" />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Phone *"><input className="ag-input" type="tel" value={user.phone || ""} onChange={(e) => setUser({ ...user, phone: e.target.value })} data-testid="wizard-phone" /></Field>
        <Field label="Professional Email *"><input className="ag-input" type="email" value={user.professional_email || ""} onChange={(e) => setUser({ ...user, professional_email: e.target.value })} data-testid="wizard-email" /></Field>
      </div>
    </>
  );
}

function Step2({ user, setUser }) {
  const status = user.license_status;
  return (
    <>
      <h2 className="font-display text-2xl text-white font-light tracking-[-0.025em]">Public Adjuster License</h2>
      <p className="text-ag-text-secondary text-sm font-light">Enter your Florida Public Adjuster license number. An AG Public Adjusting admin will verify it before your card is published.</p>
      <Field label="License Number *">
        <input className="ag-input" value={user.license_number || ""} onChange={(e) => setUser({ ...user, license_number: e.target.value })} data-testid="wizard-license" placeholder="e.g. W123456" />
      </Field>
      <div className="p-4 border border-white/10 bg-white/[0.02]">
        <div className="font-mono text-[0.55rem] tracking-[0.28em] uppercase text-gold/80 mb-2">Current status</div>
        <div className="text-sm text-white font-light">
          {status === "verified" && "✓ Verified"}
          {status === "pending" && "⏳ Pending admin verification"}
          {status === "invalid" && "⚠ Could not be verified — please contact admin"}
          {(!status || status === "not_submitted") && "Not yet submitted"}
        </div>
      </div>
      <a href="https://myfloridacfo.com/division/agents/licensee-search" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-xs text-gold hover:underline font-mono tracking-[0.18em] uppercase">
        Florida DFS Licensee Search <ExternalLink className="w-3 h-3" />
      </a>
    </>
  );
}

function Step3({ user, setUser }) {
  const langInput = (user.languages || []).join(", ");
  const areaInput = (user.service_areas || []).join(", ");
  return (
    <>
      <h2 className="font-display text-2xl text-white font-light tracking-[-0.025em]">Optional details</h2>
      <p className="text-ag-text-secondary text-sm font-light">Add languages, service area, and a short bio. All optional.</p>
      <Field label="Languages (comma-separated)">
        <input className="ag-input" value={langInput} onChange={(e) => setUser({ ...user, languages: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="wizard-languages" placeholder="English, Spanish" />
      </Field>
      <Field label="Service Areas (comma-separated)">
        <input className="ag-input" value={areaInput} onChange={(e) => setUser({ ...user, service_areas: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="wizard-service-areas" placeholder="Florida, Orlando, Tampa" />
      </Field>
      <Field label={`Short Bio (${(user.bio || "").length}/300)`}>
        <textarea rows={4} maxLength={300} className="ag-input resize-none" value={user.bio || ""} onChange={(e) => setUser({ ...user, bio: e.target.value })} data-testid="wizard-bio" />
      </Field>
    </>
  );
}

function Step4({ user, onPublish, publishing, editMode, onSubmitReview, submitted }) {
  if (editMode) {
    return (
      <>
        <h2 className="font-display text-2xl text-white font-light tracking-[-0.025em]">Review your changes</h2>
        <p className="text-ag-text-secondary text-sm font-light">Once submitted, an admin will review and approve your edits before they appear on your public card.</p>
        <PreviewCard user={user} />
        <button
          onClick={onSubmitReview}
          disabled={publishing || submitted}
          className="btn-gold w-full inline-flex items-center justify-center gap-2 !py-3.5 disabled:opacity-50"
          data-testid="wizard-submit-review"
        >
          {submitted ? "Submitted" : publishing ? "Submitting…" : "Submit Changes for Review"} <CheckCircle2 className="w-4 h-4" />
        </button>
        <p className="text-center text-ag-text-muted text-xs font-light">Your live card stays as-is until an admin approves the new version.</p>
      </>
    );
  }
  return (
    <>
      <h2 className="font-display text-2xl text-white font-light tracking-[-0.025em]">Preview</h2>
      <p className="text-ag-text-secondary text-sm font-light">Here's how your card will look.</p>
      <PreviewCard user={user} />
      <button onClick={onPublish} disabled={publishing} className="btn-gold w-full inline-flex items-center justify-center gap-2 !py-3.5 disabled:opacity-50" data-testid="wizard-publish">
        {publishing ? "Publishing…" : "Publish Card"} <CheckCircle2 className="w-4 h-4" />
      </button>
      <p className="text-center text-ag-text-muted text-xs font-light">Your card can be published now — license verification will show as "Pending" until an admin approves it.</p>
    </>
  );
}

function PreviewCard({ user }) {
  return (
    <div className="border border-gold/30 bg-black/40 p-6 text-center space-y-3" data-testid="wizard-preview">
      <img src={BRAND.logoWhite} alt="" className="h-10 w-auto object-contain mx-auto opacity-80" />
      {user.photo_url ? (
        <img src={user.photo_url} alt="" className="w-24 h-24 rounded-full object-cover mx-auto border border-gold/40" />
      ) : (
        <div className="w-24 h-24 rounded-full mx-auto border border-white/10 bg-white/5 flex items-center justify-center text-white/30 text-xs">Photo</div>
      )}
      <div className="pt-2">
        <div className="font-display text-2xl text-white font-light tracking-[-0.02em]">{user.first_name} {user.last_name}</div>
        <div className="text-ag-text-secondary text-sm font-light">{user.professional_title || "Licensed Public Adjuster"}</div>
        {user.license_number && <div className="font-mono text-[0.6rem] text-gold/80 tracking-[0.22em] mt-2">Lic. {user.license_number}</div>}
      </div>
    </div>
  );
}
